import axios from 'axios';
import { API_BASE_URL } from '@/lib/apiBase';
import { endSession, refreshTokens } from '@/lib/authSession';
import { tokenStore } from '@/lib/tokenStore';

export { API_BASE_URL };

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 20000,
  headers: { 'Content-Type': 'application/json' },
});

/** Normalised error shape every caller can rely on. */
export class ApiError extends Error {
  constructor({ status, code, message, details, correlationId }) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.correlationId = correlationId;
  }

  /** Field errors keyed by field name, for react-hook-form's setError. */
  get fieldErrors() {
    if (!Array.isArray(this.details)) return {};
    return this.details.reduce((acc, item) => {
      if (item?.field) acc[item.field] = item.message;
      return acc;
    }, {});
  }
}

function toApiError(error) {
  if (axios.isCancel(error)) {
    return new ApiError({ status: 0, code: 'cancelled', message: 'Request cancelled' });
  }
  if (!error.response) {
    return new ApiError({
      status: 0,
      code: 'network_error',
      message: 'Could not reach the server. Check your connection and try again.',
    });
  }
  // The API always answers with { error: { code, message, details, correlation_id } }.
  const { status, data } = error.response;
  const payload = data?.error ?? {};
  return new ApiError({
    status,
    code: payload.code || 'unknown_error',
    message: payload.message || 'Something went wrong.',
    details: payload.details,
    correlationId: payload.correlation_id,
  });
}

// Let the browser set its own multipart boundary.
apiClient.interceptors.request.use((config) => {
  if (config.data instanceof FormData) delete config.headers['Content-Type'];
  // Which shop this is. Each customer's website is a subdomain
  // (acme.example.com), and the API cannot rely on the Host header to say so:
  // the dev proxy rewrites it, and a split deployment calls the API on its own
  // hostname.
  config.headers['X-Storefront-Host'] = window.location.host;
  const token = tokenStore.getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    const isRefreshable =
      status === 401 &&
      original &&
      !original._retried &&
      !original.skipAuthRefresh &&
      !String(original.url || '').includes('/auth/');

    if (isRefreshable) {
      original._retried = true;
      // The token this request carried: if another tab has replaced it since,
      // refreshTokens hands back that one instead of rotating again.
      const stale = String(original.headers?.Authorization ?? '').replace(/^Bearer /, '');
      try {
        // Shared by every request that got a 401, in this tab and others, so
        // ten concurrent calls cause one refresh rather than ten.
        const token = await refreshTokens(stale || undefined);
        original.headers.Authorization = `Bearer ${token}`;
        return apiClient(original);
      } catch {
        // Every tab signs out, and the router reacts rather than this code
        // hard-navigating, so a storefront visitor is not bounced off the page.
        endSession('expired');
      }
    }

    // The shop's subscription ran out while this tab was open. Ask /me again
    // so the route guard moves the user to the subscription page.
    if (status === 403 && error.response?.data?.error?.details?.subscription_expired) {
      window.dispatchEvent(new CustomEvent('auth:subscription-expired'));
    }

    return Promise.reject(toApiError(error));
  },
);

/** Unwraps the { data, meta } envelope. */
export async function request(config) {
  const response = await apiClient(config);
  return response.data?.data !== undefined ? response.data.data : response.data;
}

/** Like `request`, but keeps pagination metadata for list endpoints. */
export async function requestPage(config) {
  const response = await apiClient(config);
  return { items: response.data?.data ?? [], meta: response.data?.meta ?? null };
}
